import { zodResolver } from '@hookform/resolvers/zod'
import type { inferRouterOutputs } from '@trpc/server'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import useDeepCompareEffect from 'use-deep-compare-effect'
import { uuidv7 } from 'uuidv7'

import type { AppRouter } from '@rescuebase/api/client-types'

import { prepareFormForSubmission } from '@/components/form/formUtils'
import fosterFormSchema, {
  FosterForm,
} from '@/components/fosters/fosterFormSchema'
import Snackbar from '@/components/snackbar/Snackbar'
import WeightUnit from '@/enums/weightUnit'
import useAutoSave from '@/hooks/useAutoSave'
import FosterService from '@/services/fosterService'
import trpc from '@/utils/trpc'

type RouterOutput = inferRouterOutputs<AppRouter>

// We only include the explicit fields in our form, because the foster response is
// much more complicated and will trigger infinite re-renders in our autosave hook otherwise.
const fosterDefaults = (
  foster:
    | RouterOutput['foster']['byId']
    | RouterOutput['foster']['update']
    | undefined
) => {
  if (!foster) {
    return {}
  }

  const weightUnit = foster.weightHistory?.[0]?.weightUnit || WeightUnit.Pounds

  // TODO - weightHistory - we can stop checking for `foster.weight` after the weightHistory migration is complete
  const weight =
    foster.weightHistory?.[0]?.weight ||
    (foster.weight ? foster.weight * 16 : undefined)

  return {
    birthDate: foster?.birthDate,
    breed: foster?.breed,
    deceasedDate: foster?.deceasedDate,
    clearFecalTestDate: foster?.clearFecalTestDate,
    comboTestingCompletedDate: foster?.comboTestingCompletedDate,
    currentFood: foster?.currentFood,
    fecalFollowUpDate: foster?.fecalFollowUpDate,
    felvPositive: foster?.felvPositive,
    fivPositive: foster?.fivPositive,
    fixedBeforeIntake: foster?.fixedBeforeIntake,
    fixedOnDate: foster?.fixedOnDate,
    gender: foster?.gender,
    heartwormPositive: foster?.heartwormPositive,
    heartwormTestingCompletedDate: foster?.heartwormTestingCompletedDate,
    history: foster?.history,
    intakeDate: foster?.intakeDate,
    isAdopted: foster?.isAdopted,
    isHospice: foster?.isHospice,
    microchipId: foster?.microchipId,
    name: foster?.name,
    notes: foster?.notes,
    positiveFecalTestDate: foster?.positiveFecalTestDate,
    privateNotes: foster?.privateNotes,
    readyForAdoption: foster?.readyForAdoption,
    species: foster?.species,
    training: foster?.training,
    userId: foster?.user?.id as string,
    vaccinations:
      foster?.vaccinations?.map((v) => ({
        id: v.id || uuidv7(),
        vaccineId: v.vaccineId,
        fosterId: foster.id,
        dueDate: v.dueDate,
        givenDate: v.givenDate,
        givenBy: v.givenBy,
      })) || [],
    medications:
      foster?.medications?.map((m) => ({
        id: m.id || uuidv7(),
        fosterId: foster.id,
        name: m.name || '',
        dueDate: m.dueDate,
        givenDate: m.givenDate,
        givenBy: m.givenBy,
        notes: m.notes,
        isRepeating: m.isRepeating,
        frequency: m.frequency,
      })) || [],
    weight: weight,
    weightUnit: weightUnit,
  }
}

const useFosterForm = (fosterId: string) => {
  const { foster, refreshFoster } = FosterService.useFoster(fosterId)
  const { refreshFosterList } = FosterService.useFosterList()
  const skipNextAutoSave = useRef(false)
  // Add refs to track original weight and unit
  const originalWeight = useRef<number | null | undefined>(undefined)
  const originalUnit = useRef<string | undefined>(undefined)

  const fosterUpdateMutation = trpc.foster.update.useMutation()

  const [defaultValues, setDefaultValues] = useState(fosterDefaults(foster))

  const form = useForm<FosterForm>({
    mode: 'onBlur',
    resolver: zodResolver(fosterFormSchema),
    defaultValues: defaultValues,
  })

  const { formState, handleSubmit, reset, setValue, getValues, watch } = form

  // When a user changes the weight unit and a weight already exists, we want to clear the form field but avoid auto-saving
  const subscription = watch((value, { name }) => {
    if (name === 'weightUnit') {
      if (value.weightUnit !== defaultValues.weightUnit) {
        skipNextAutoSave.current = true

        // Store original values if we haven't yet
        if (!originalUnit.current) {
          originalUnit.current = defaultValues.weightUnit
          originalWeight.current = getValues('weight')
        }

        // Clear both the form value and default value to prevent flickering
        setValue('weight', null, { shouldDirty: true })
        // Use undefined in defaultValues to satisfy TypeScript
        setDefaultValues((prev) => ({
          ...prev,
          weight: undefined,
        }))
      }

      // If switching back to original unit, restore the original values in refs
      // but keep the input empty, and don't trigger auto-save
      if (value.weightUnit === originalUnit.current) {
        skipNextAutoSave.current = true
        setValue('weight', originalWeight.current, { shouldDirty: false })
        originalUnit.current = undefined
        originalWeight.current = undefined
      }
    }
  })

  useEffect(() => {
    return () => {
      subscription.unsubscribe()
    }
  }, [subscription])

  //   return () => {
  //     console.log('skipNextAutoSave 3')
  //     skipNextAutoSave.current = true
  //     subscription.unsubscribe()
  //   }
  // }, [watch, setValue, defaultValues.weightUnit, getValues, form])

  // This looks complex (and it is a bit), but it's necessary to do this manually with reset so that we don't
  // unmount and remount the vaccinations `useFieldArray` field, which causes the UI to have jank while users are editing vaccines
  useDeepCompareEffect(() => {
    Object.entries(defaultValues).forEach(([key, value]) => {
      if (key === 'vaccinations') {
        const vaccinations: FosterForm['vaccinations'] = (value ||
          []) as FosterForm['vaccinations']

        if (!vaccinations.length) {
          setValue('vaccinations', [], { shouldDirty: false })
        }

        const existingVaccinations = getValues().vaccinations

        if (
          existingVaccinations?.length === 0 &&
          !foster?.vaccinations?.length
        ) {
          return
        }

        // We _need_ to reset the form if the number of vaccines changes,
        // otherwise the `useFieldArray` vaccinations field seems to become uncontrolled
        if (
          (existingVaccinations?.length || -1) <
          (foster?.vaccinations?.length || 0)
        ) {
          reset(defaultValues)
          return
        }

        for (let i = 0; i < existingVaccinations?.length; i++) {
          const vaccination = foster?.vaccinations.find(
            (v) => v.id === existingVaccinations[i].id
          )

          if (!vaccination) {
            continue
          }

          if (!formState.dirtyFields.vaccinations?.[i]?.id) {
            setValue(`vaccinations.${i}.id` as const, vaccination.id)
          }

          if (!formState.dirtyFields.vaccinations?.[i]?.fosterId) {
            setValue(`vaccinations.${i}.fosterId` as const, fosterId)
          }

          if (!formState.dirtyFields.vaccinations?.[i]?.vaccineId) {
            setValue(
              `vaccinations.${i}.vaccineId` as const,
              vaccination.vaccineId
            )
          }

          if (!formState.dirtyFields.vaccinations?.[i]?.dueDate) {
            setValue(
              `vaccinations.${i}.dueDate` as const,
              vaccination.dueDate || null
            )
          }

          if (!formState.dirtyFields.vaccinations?.[i]?.givenDate) {
            setValue(
              `vaccinations.${i}.givenDate` as const,
              vaccination.givenDate || null
            )
          }

          if (!formState.dirtyFields.vaccinations?.[i]?.givenBy) {
            setValue(
              `vaccinations.${i}.givenBy` as const,
              vaccination.givenBy || null
            )
          }
        }
      } else if (key === 'medications') {
        const medications: FosterForm['medications'] = (value ||
          []) as FosterForm['medications']

        if (!medications.length) {
          setValue('medications', [], { shouldDirty: false })
        }

        const existingMedications = getValues().medications

        if (existingMedications?.length === 0 && !foster?.medications?.length) {
          return
        }

        // We _need_ to reset the form if the number of medications changes,
        // otherwise the `useFieldArray` medications field seems to become uncontrolled
        if (
          (existingMedications?.length || -1) <
          (foster?.medications?.length || 0)
        ) {
          reset(defaultValues)
          return
        }

        for (let i = 0; i < existingMedications?.length; i++) {
          const medication = foster?.medications.find(
            (v) => v.id === existingMedications[i].id
          )

          if (!medication) {
            continue
          }

          if (!formState.dirtyFields.medications?.[i]?.id) {
            setValue(`medications.${i}.id` as const, medication.id)
          }

          if (!formState.dirtyFields.medications?.[i]?.fosterId) {
            setValue(`medications.${i}.fosterId` as const, fosterId)
          }

          if (!formState.dirtyFields.medications?.[i]?.name) {
            setValue(`medications.${i}.name` as const, medication.name || '')
          }

          if (!formState.dirtyFields.medications?.[i]?.dueDate) {
            setValue(
              `medications.${i}.dueDate` as const,
              medication.dueDate || null
            )
          }

          if (!formState.dirtyFields.medications?.[i]?.givenDate) {
            setValue(
              `medications.${i}.givenDate` as const,
              medication.givenDate || null
            )
          }

          if (!formState.dirtyFields.medications?.[i]?.givenBy) {
            setValue(
              `medications.${i}.givenBy` as const,
              medication.givenBy || null
            )
          }

          if (!formState.dirtyFields.medications?.[i]?.notes) {
            setValue(
              `medications.${i}.notes` as const,
              medication.notes || null
            )
          }

          if (!formState.dirtyFields.medications?.[i]?.frequency) {
            setValue(
              `medications.${i}.frequency` as const,
              medication.frequency || null
            )
          }

          if (!formState.dirtyFields.medications?.[i]?.isRepeating) {
            setValue(
              `medications.${i}.isRepeating` as const,
              medication.isRepeating || false
            )
          }
        }
      } else {
        if (!formState.dirtyFields[key as keyof FosterForm]) {
          setValue(key as keyof FosterForm, value, { shouldDirty: false })
        }
      }
    })
  }, [defaultValues])

  useEffect(() => {
    if (foster) {
      setDefaultValues(fosterDefaults(foster))
    }
  }, [foster])

  const onSubmit = () => {
    handleSubmit((data) => {
      fosterUpdateMutation.mutate(
        {
          ...prepareFormForSubmission(data),
          id: fosterId,
          weight: data.weight ? parseFloat(data.weight.toString()) : null,
          vaccinations: [...(data.vaccinations || [])],
          medications: [...(data.medications || [])],
        },
        {
          onError: () => {
            Snackbar.error('There was a problem saving this foster')
          },
          onSuccess: async () => {
            // TODO: is this necessary anymore?
            await refreshFoster()

            // Refresh list so the grid view shows updated info, like name, volunteer, etc.
            await refreshFosterList()
          },
        }
      )
    })()
  }

  useAutoSave({
    form,
    defaultValues,
    onSave: () => {
      if (skipNextAutoSave.current) {
        skipNextAutoSave.current = false
        return
      }
      onSubmit()
    },
  })

  return form
}

export default useFosterForm
